import {get} from 'lodash-es';

import {EFormMode} from 'components/form';
import {TEntity} from 'modules/data';
import {combineString} from 'shared/utils/combine-string';

import {EFieldType, EMetaActionType} from './metadata-constants';
import {
    ActionVisibility, IMetaActionInfo, TReferenceResolver, TVisibleMetaActionProp,
} from './metadata-types';

export const getFormInitValue = (type: EFieldType) => {
    switch (type) {
    case EFieldType.DATE:
        return null;
    case EFieldType.TEXTAREA:
    case EFieldType.STRING:
        return '';
    case EFieldType.BOOLEAN:
        return 'N';
    case EFieldType.BOOLEAN_CLASSIC:
        return false;
    case EFieldType.SIGNATURE:
    case EFieldType.FILE:
    case EFieldType.ICON:
    case EFieldType.NUMBER:
    case EFieldType.COUNTER:
    case EFieldType.REFERENCE:
    case EFieldType.LIST:
    case EFieldType.FIELDS_TREE:
    default:
        return undefined;
    }
};

export const getActionByType = (type: EMetaActionType) => (
    actions?: IMetaActionInfo[],
): IMetaActionInfo | undefined => actions
    ?.find(action => action.actionType === type);

export const getReferenceResolverFunction = (
    labelPath: string,
    valuePath: string,
    reserveLabelPath?: string,
    lookupCode?: string,
    stringStructure?: string,
): TReferenceResolver => (record: TEntity) => {
    const onNotCreateKey = record?.onNotCreateKey;
    const isSetFilter = record?.isSetFilter;
    const displayFieldKey = record?.displayFieldKey;
    const dependentInputKey = record?.dependentInputKey;
    const additionalInputOptions = record?.additionalInputOptions;
    let label = stringStructure
        ? combineString(stringStructure, {lookupValue: {...record}}) || ''
        : get(record, labelPath) as string;
    let value = get(record, valuePath) as string | number;

    if (displayFieldKey) {
        value = record?.[displayFieldKey as string] as string | number;
    }

    if (!value) {
        value = record?.value as string | number;
    }

    if (!label && reserveLabelPath) {
        label = get(record, reserveLabelPath) as string;
    }
    return {
        ...record,
        label,
        value,
        onNotCreateKey,
        isSetFilter,
        displayFieldKey,
        dependentInputKey,
        additionalInputOptions,
    };
};

export const createVisibleEqualityPredicate = (currentState: TVisibleMetaActionProp) => (
    visibleState: TVisibleMetaActionProp = 'default',
) => (
    visibleState === currentState
);
/**
 * Возвращает видимость кнопок в зависимости от текущего состояния формы по следующим правилам:
 * default - все кнопки видны
 * edit - видны только кнопки редактирования (c visible = edit)
 * viewing - видны только кнопки просмотра (c visible = viewing)
 * @param currentVisible
 * @param visible
 * @param formMode в теории может быть использован для показа кнопок в зависимости от режима формы
 */
export const isVisibleAction = (
    currentVisible: TVisibleMetaActionProp = ActionVisibility.DEFAULT,
    visible: TVisibleMetaActionProp = ActionVisibility.DEFAULT,
    formMode?: EFormMode,
) => (visible === currentVisible
    || (currentVisible === ActionVisibility.DEFAULT || visible === ActionVisibility.DEFAULT) // default = always show
    || (currentVisible === ActionVisibility.EDITING && visible === ActionVisibility.EDITING) // edit = show edit
    || (currentVisible === ActionVisibility.VIEWING && visible === ActionVisibility.VIEWING) // view = show view
    || (visible === ActionVisibility.EDITING && formMode === 'edit' && currentVisible === ActionVisibility.DEFAULT)
    || (visible === ActionVisibility.CREATION && formMode === 'create' && currentVisible === ActionVisibility.DEFAULT));
